import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { handlePagingAll } from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";

import { Formik } from "formik";
import {
  BCPArrowLeft,
  BCPLinkSimple,
  BCPSquarePlus,
  BCPUserMagnifyingGlass,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";

export default function OrganizationUsers(props) {
  const { setOrganization, organization } = props;

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);
  const [organizationUsers, setOrganizationUsers] = useState(null);
  const [organizationUser, setOrganizationUser] = useState(null);

  useEffect(() => {
    const check = async () => {
      var apiUsers = await handlePagingAll(`/gamestoreusers`, {
        limit: 100,
        gameStoreId: organization.id,
        "expand[]": ["user"],
      });
      setOrganizationUsers(apiUsers);
      setSubLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2, p: 2 }}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Link
              href="/organize/game-stores"
              color="link.main"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <BCPArrowLeft
                sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
              />{" "}
              Back to My Organizations
            </Link>
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
            >
              Personnel
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <ListResponsive
              originalList={subLoading ? null : organizationUsers}
              doSearch
              doPages
              initialRowsPerPage={25}
              actionButton={[
                {
                  props: {
                    variant: "contained",
                    color: "primary",
                    onClick: () => {
                      setOrganizationUser({});
                    },
                    startIcon: <BCPSquarePlus />,
                  },
                  label: "Add User",
                },
              ]}
              searchProps={{
                label: "Search Users",
                performSearch: (originalList, searchText) => {
                  if (!searchText || searchText === "") {
                    return originalList;
                  } else {
                    var filteredList = originalList.filter(function (el) {
                      if (
                        el?.user?.firstName?.toLowerCase().includes(searchText)
                      )
                        return true;
                      if (
                        el?.user?.lastName?.toLowerCase().includes(searchText)
                      )
                        return true;
                      if (el?.user?.email?.toLowerCase().includes(searchText))
                        return true;

                      return false;
                    });
                    return filteredList;
                  }
                },
              }}
              rowProps={{
                mapFunction: (row, index) => (
                  <Grid
                    key={index}
                    item
                    xs={12}
                    onClick={(e) => {
                      setOrganizationUser(row);
                    }}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Typography variant="body1">
                      {`${row.user?.firstName} ${row.user?.lastName}`}
                    </Typography>
                  </Grid>
                ),
              }}
            />
          </Grid>
        </Grid>
        <OrganizationUserModal
          organization={organization}
          setOrganization={setOrganization}
          organizationUser={organizationUser}
          setOrganizationUser={setOrganizationUser}
          organizationUsers={organizationUsers}
          setOrganizationUsers={setOrganizationUsers}
          setProcessing={setProcessing}
        />

        {Boolean(processing) && <ModalLoadingFull />}
      </Container>
    </Box>
  );
}

function OrganizationUserModal(props) {
  const {
    organization,
    setOrganization,
    organizationUser,
    setOrganizationUser,
    organizationUsers,
    setOrganizationUsers,
    setProcessing,
  } = props;

  const isOwner =
    organization?.ownerId && organizationUser?.userId === organization?.ownerId;

  const searchUser = async (email, setFieldValue) => {
    var apiUser;
    if (email !== "") {
      setProcessing(true);
      try {
        apiUser = await getData(`/users/${email}`, {
          queryStringParameters: {},
        });
      } catch (err) {}
      setProcessing(false);
    }

    if (
      apiUser?.id &&
      organizationUsers.filter(function (el) {
        return el?.userId === apiUser.id;
      }).length > 0
    ) {
      postNotification("error", "User is already assigned to the Organization");

      setFieldValue("userId", null);
      setFieldValue("firstName", "");
      setFieldValue("lastName", "");
      return;
    }
    setFieldValue("userId", apiUser?.id || null);
    setFieldValue("firstName", apiUser?.firstName || "");
    setFieldValue("lastName", apiUser?.lastName || "");

    if (!apiUser?.id) {
      postNotification("error", "User not found");
    }
  };

  return (
    <Modal
      disableAutoFocus
      open={Boolean(organizationUser)}
      onClose={() => {
        setOrganizationUser(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      {organizationUser ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOrganizationUser(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {isOwner
                  ? "Store Owner"
                  : organizationUser.id
                  ? "Update User"
                  : "Create User"}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              userId: organizationUser?.userId || null,
              email: organizationUser?.user?.email || "",
              firstName: organizationUser?.user?.firstName || "",
              lastName: organizationUser?.user?.lastName || "",
              active: isOwner
                ? true
                : organizationUser.active !== undefined
                ? organizationUser.active
                : true,
              updateStore: isOwner
                ? true
                : organizationUser.updateStore || false,
              manageUsers: isOwner
                ? true
                : organizationUser.manageUsers || false,
              isTO: isOwner ? true : organizationUser.isTO || false,
              createLeagues: isOwner
                ? true
                : organizationUser.createLeagues || false,
              approveTokens: isOwner
                ? true
                : organizationUser.approveTokens || false,
              corrections: isOwner
                ? true
                : organizationUser.corrections || false,
            }}
            enableReinitialize
            onSubmit={async (values) => {
              var apiData;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  ...values,
                };

                delete sanitizedValues.firstName;
                delete sanitizedValues.lastName;
                delete sanitizedValues.email;

                if (organizationUser.id) {
                  var body = {
                    set: sanitizedValues,
                  };
                  apiData = await postData(
                    `/gamestoreusers/${organizationUser.id}`,
                    {
                      body: body,
                    }
                  );
                } else {
                  sanitizedValues.gameStoreId = organization.id;
                  apiData = await postData(`/gamestoreusers`, {
                    body: sanitizedValues,
                  });
                }

                apiData.user = {
                  id: apiData.userId,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email,
                };

                var newUsers;
                if (organizationUser.id) {
                  newUsers = [];
                  organizationUsers.forEach((currentUser) => {
                    newUsers.push(
                      currentUser.id === organizationUser.id
                        ? apiData
                        : currentUser
                    );
                  });

                  setOrganization({
                    ...organization,
                    myStoreUser: apiData,
                  });
                } else {
                  newUsers = [apiData, ...organizationUsers];
                }
                setOrganizationUsers(newUsers);

                setOrganizationUser(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled={Boolean(organizationUser.id)}
                      required
                      InputProps={{
                        endAdornment: organizationUser.id ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Copy Email"
                              edge="end"
                              color="primary"
                              onClick={() => {
                                navigator.clipboard.writeText(values.email);
                              }}
                            >
                              <BCPLinkSimple />
                            </IconButton>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Search"
                              edge="end"
                              color="primary"
                              onClick={async () => {
                                await searchUser(values.email, setFieldValue);
                              }}
                            >
                              <BCPUserMagnifyingGlass />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        await searchUser(e.target.value, setFieldValue);
                      }}
                      onChange={(e) => {
                        const trimmedString = e.target.value
                          .trim()
                          .toLowerCase();
                        setFieldValue("email", trimmedString);
                      }}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="First Name"
                      value={values.firstName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Name"
                      value={values.lastName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Active"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.active}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue("active", !values.active);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Update the Organization"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.updateStore}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue("updateStore", !values.updateStore);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Tournament Organizer"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.isTO}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue("isTO", !values.isTO);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Manage Users"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.manageUsers}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue("manageUsers", !values.manageUsers);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Manage Circuits"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.createLeagues}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue(
                              "createLeagues",
                              !values.createLeagues
                            );
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Manage Circuit Corrections"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.corrections}
                          disabled={isOwner}
                          onChange={(e) => {
                            setFieldValue("corrections", !values.corrections);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  {organization?.circuitAccess === true && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Manage Tokens"
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)",
                          },
                        }}
                        control={
                          <Switch
                            checked={values.approveTokens}
                            disabled={isOwner}
                            onChange={(e) => {
                              setFieldValue(
                                "approveTokens",
                                !values.approveTokens
                              );
                            }}
                          />
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                  )}

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setOrganizationUser(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={isOwner || !values.userId}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
